import { useInfiniteGraphqlQuery } from "graphql-mock"
import { Button } from "materia"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { modifiers } from "ui-tools"
import { FiestaImage, WhenVisible } from "utility-components"
import { query } from "../../../components/FilteredProducts/gql"
import type { ProductTypeIdentifier } from "../../../graphql"
import { ChatModal } from "../ChatModal"
import s from "./SharePicker.module.scss"

const ITEMS_PER_PAGE = 18

/** The learning item the sender picked to share — enough to build the wire ref. */
export type PickedProduct = {
  id: string
  productType: ProductTypeIdentifier
}

type SharePickerProps = {
  onShare: (product: PickedProduct) => void
  onClose: () => void
}

export function SharePicker({ onShare, onClose }: SharePickerProps) {
  const { t } = useTranslation()
  const [search, setSearch] = useState("")
  const [selected, setSelected] = useState<PickedProduct>()

  const { data, isPending, error, fetchNextPage, isFetchingNextPage } =
    useInfiniteGraphqlQuery(
      query,
      { input: { variant: "explore", search }, limit: ITEMS_PER_PAGE },
      { getNextPage: (page) => page.products.pageInfo.nextPage }
    )

  const products = data?.pages.flatMap((page) => page.products.nodes) ?? []

  return (
    <ChatModal ariaLabel={String(t("chat.SHARE_TITLE"))} onClose={onClose}>
      <div className={s.picker}>
        <h2 className={s.picker__title}>{t("chat.SHARE_TITLE")}</h2>

        <input
          className={s.picker__search}
          value={search}
          placeholder={String(t("chat.SHARE_SEARCH_PLACEHOLDER"))}
          onChange={(event) => setSearch(event.target.value)}
        />

        <div className={s.picker__list}>
          {error ? (
            <p className={s.picker__empty}>{t("chat.SHARE_ERROR")}</p>
          ) : isPending ? (
            <p className={s.picker__empty}>{t("chat.SHARE_LOADING")}</p>
          ) : products.length === 0 ? (
            <p className={s.picker__empty}>{t("chat.SHARE_NO_RESULTS")}</p>
          ) : (
            products.map((product) => (
              <button
                key={product.id}
                className={modifiers(s, "picker__row", {
                  isSelected: selected?.id === product.id,
                })}
                type="button"
                aria-pressed={selected?.id === product.id}
                onClick={() =>
                  setSelected({
                    id: product.id,
                    productType: product.productType,
                  })
                }
              >
                <FiestaImage
                  className={s.picker__thumb}
                  src={product.image}
                  alt=""
                  loading="lazy"
                  sizes="48px"
                />
                <div className={s.picker__text}>
                  <span className={s.picker__type}>
                    {t(`product.PRODUCT_TYPES.${product.productType}`)}
                  </span>
                  <span className={s.picker__name}>{product.title}</span>
                </div>
              </button>
            ))
          )}

          <WhenVisible
            key={`${products.length} ${search}`}
            fetchNextPage={fetchNextPage}
            isFetchingNextPage={isFetchingNextPage}
          />
        </div>

        <Button
          theme="blue"
          size="big"
          borderRadius="24"
          disabled={!selected}
          onClick={() => selected && onShare(selected)}
        >
          {t("chat.SHARE_CONTINUE")}
        </Button>
      </div>
    </ChatModal>
  )
}
